import { gql, useMutation } from '@apollo/client';
import { useDispatch, useI18n, useLogger, useSelector } from '~Hooks';
import { PrintInQueue } from '~reducers';
import * as actions from '~actions';
import { generateCommands } from '~Components/PrinterManager/utils';
import { CloudPrintFromQueue, CloudPrintFromQueueVariables } from './__queries__';

const CLOUD_PRINT_MUTATION = gql`
  mutation CloudPrintFromQueue(
    $printerId: String
    $venueId: String
    $image: String
    $text: String
    $commands: String
    $printType: String
    $orderId: String
  ) {
    cloudPrint(
      printerId: $printerId
      venueId: $venueId
      image: $image
      text: $text
      commands: $commands
      printType: $printType
      orderId: $orderId
    ) {
      message
      success
      error
    }
  }
`;

/**
 * Hook that provides a function to send print jobs to Star cloud printers
 * via backend GraphQL mutation. Retries up to 4 times on network failure.
 */
const useCloudPrint = () => {
  const logger = useLogger('printer');
  const dispatch = useDispatch();
  const venueId = useSelector((state) => state.venueId!);
  const [cloudPrintMutation] = useMutation<CloudPrintFromQueue, CloudPrintFromQueueVariables>(
    CLOUD_PRINT_MUTATION,
  );

  const sendCloudPrint = async (nxtPrint: PrintInQueue) => {
    const maxRetries = 4;
    const retryInterval = 5000;

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        let res;

        if (nxtPrint.base64Image) {
          res = await cloudPrintMutation({
            variables: {
              printerId: nxtPrint.printer,
              venueId,
              image: nxtPrint.base64Image.replace('data:image/png;base64,', ''),
              printType: nxtPrint.printType,
              orderId: nxtPrint.orderId,
            },
          });
        } else if (typeof nxtPrint.data === 'string') {
          res = await cloudPrintMutation({
            variables: {
              printerId: nxtPrint.printer,
              venueId,
              text: nxtPrint.data,
              printType: nxtPrint.printType,
              orderId: nxtPrint.orderId,
            },
          });
        } else if (nxtPrint.data) {
          const commands = generateCommands(nxtPrint.data);
          res = await cloudPrintMutation({
            variables: {
              printerId: nxtPrint.printer,
              venueId,
              commands,
              printType: nxtPrint.printType,
              orderId: nxtPrint.orderId,
            },
          });
        }

        if (!res?.data?.cloudPrint?.success) {
          logger.warn('Cloud print backend rejected the request', {
            queueId: nxtPrint.id,
            cloudPrintError: res?.data?.cloudPrint?.error,
            attempt,
          });
          dispatch({
            type: actions.MESSAGE_PRINT_BRIDGE_REQUEST_FAILED,
            id: nxtPrint.id,
            error: res?.data?.cloudPrint?.error || 'Cloud print failed',
          });
          return;
        }

        logger.info('Cloud print sent successfully', {
          queueId: nxtPrint.id,
          attempt,
        });

        // Mark as successful in the queue — the job is now pending on the backend
        dispatch({
          type: actions.MESSAGE_PRINT_BRIDGE_REQUEST_SUCCESS,
          id: nxtPrint.id,
        });
        return;
      } catch (e) {
        const isLastAttempt = attempt === maxRetries - 1;

        if (isLastAttempt) {
          logger.error('Cloud print failed after all retries', {
            queueId: nxtPrint.id,
            errorMsg: (e as Error).message,
            totalAttempts: maxRetries,
          });
          dispatch({
            type: actions.MESSAGE_PRINT_BRIDGE_REQUEST_FAILED,
            id: nxtPrint.id,
            error: 'Cloud print failed: network error after ' + maxRetries + ' retries',
          });
          return;
        }

        logger.info('Retrying cloud print', {
          queueId: nxtPrint.id,
          errorMsg: (e as Error).message,
          attempt,
          nextRetryIn: `${retryInterval / 1000}s`,
        });

        await new Promise((resolve) => setTimeout(resolve, retryInterval));
      }
    }
  };

  return { sendCloudPrint };
};

export default useCloudPrint;
